export type SiteBrandGuardInput = {
  id: string;
  label: string;
  domain?: string | null;
  frontendUrl?: string | null;
};

export type ForeignSiteMention = {
  siteId: string;
  label: string;
  matched: string;
};

function normalizeHost(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .replace(/\/.*$/, '')
    .replace(/:\d+$/, '');
}

function brandFingerprint(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '');
}

function stripHtmlForScan(content: string): string {
  return content
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/\s+/g, ' ');
}

/** Brand tokens used to detect a website mention in email content. */
export function collectSiteBrandMarkers(site: SiteBrandGuardInput): string[] {
  const markers = new Set<string>();
  const label = site.label?.trim();
  if (label) {
    markers.add(label.toLowerCase());
    const compact = brandFingerprint(label);
    if (compact.length >= 5) markers.add(compact);
  }
  if (site.domain?.trim()) {
    const host = normalizeHost(site.domain);
    markers.add(host);
    const hostCompact = brandFingerprint(host);
    if (hostCompact.length >= 5) markers.add(hostCompact);
  }
  if (site.frontendUrl?.trim()) {
    const host = normalizeHost(site.frontendUrl);
    markers.add(host);
    markers.add(`https://${host}`);
    markers.add(`http://${host}`);
    const hostCompact = brandFingerprint(host);
    if (hostCompact.length >= 5) markers.add(hostCompact);
  }
  return [...markers]
    .map((marker) => marker.trim().toLowerCase())
    .filter((marker) => marker.length >= 4);
}

function extractHostsFromContent(content: string): string[] {
  const hosts = new Set<string>();
  const urlRegex = /https?:\/\/[^\s"'<>]+/gi;
  const emailRegex = /[a-z0-9._%+-]+@([a-z0-9.-]+\.[a-z]{2,})/gi;
  let match: RegExpExecArray | null;
  while ((match = urlRegex.exec(content)) !== null) {
    try {
      hosts.add(normalizeHost(new URL(match[0]).hostname));
    } catch {
      hosts.add(normalizeHost(match[0]));
    }
  }
  while ((match = emailRegex.exec(content)) !== null) {
    hosts.add(normalizeHost(match[1]));
  }
  return [...hosts];
}

function contentContainsMarker(scanText: string, fingerprintText: string, marker: string): boolean {
  if (!marker) return false;
  if (marker.includes('.') || marker.startsWith('http')) {
    return scanText.includes(marker);
  }
  // Compact alphanumeric marker: match against fingerprint of whole content
  if (/^[a-z0-9]+$/.test(marker) && marker.length >= 5) {
    return fingerprintText.includes(marker);
  }
  const escaped = marker.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(escaped, 'i').test(scanText);
}

/**
 * Finds configured websites (other than the selected one) mentioned in subject/body.
 */
export function findForeignSiteMentions(
  content: string,
  selectedSiteId: string,
  sites: SiteBrandGuardInput[]
): ForeignSiteMention[] {
  const text = String(content || '');
  if (!text.trim() || sites.length === 0) return [];

  const selected = sites.find((site) => site.id === selectedSiteId);
  const allowed = new Set(selected ? collectSiteBrandMarkers(selected) : []);
  const scanText = `${text}\n${stripHtmlForScan(text)}`.toLowerCase();
  const fingerprintText = brandFingerprint(stripHtmlForScan(text));
  const contentHosts = extractHostsFromContent(text);
  const found: ForeignSiteMention[] = [];

  for (const site of sites) {
    if (site.id === selectedSiteId) continue;
    const markers = collectSiteBrandMarkers(site)
      .filter((marker) => !allowed.has(marker))
      .sort((a, b) => b.length - a.length);

    let matched: string | null = null;
    for (const marker of markers) {
      if (contentContainsMarker(scanText, fingerprintText, marker)) {
        matched = marker;
        break;
      }
    }

    if (!matched) {
      const siteHosts = new Set(
        [site.domain, site.frontendUrl]
          .filter(Boolean)
          .map((value) => normalizeHost(String(value)))
      );
      for (const host of contentHosts) {
        if (siteHosts.has(host) && !allowed.has(host)) {
          matched = host;
          break;
        }
      }
    }

    if (matched) {
      found.push({
        siteId: site.id,
        label: site.label,
        matched,
      });
    }
  }

  return found;
}

export function formatForeignSiteContentError(
  selectedLabel: string,
  mentions: ForeignSiteMention[]
): string {
  const names = [...new Set(mentions.map((m) => m.label))];
  if (names.length === 0) {
    return `This message must only use ${selectedLabel} branding.`;
  }
  return `This message mentions ${names.join(', ')}, but ${selectedLabel} is selected. Remove other website names and links, or switch the website above.`;
}

export function validateContentForSelectedSite(
  content: string,
  selectedSiteId: string,
  sites: SiteBrandGuardInput[]
): { ok: true } | { ok: false; error: string; mentions: ForeignSiteMention[] } {
  const selected = sites.find((site) => site.id === selectedSiteId);
  if (!selectedSiteId || !selected) {
    return {
      ok: false,
      error: 'Choose a website before saving or sending.',
      mentions: [],
    };
  }
  const mentions = findForeignSiteMentions(content, selectedSiteId, sites);
  if (mentions.length === 0) return { ok: true };
  return {
    ok: false,
    error: formatForeignSiteContentError(selected.label, mentions),
    mentions,
  };
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Rewrites other configured website names/links to the selected website branding.
 */
export function rewriteContentToSelectedSite(
  content: string,
  selectedSiteId: string,
  sites: SiteBrandGuardInput[]
): string {
  const selected = sites.find((site) => site.id === selectedSiteId);
  if (!selected || !content) return content;

  const selectedLabel = selected.label.trim();
  const selectedHost = normalizeHost(selected.frontendUrl || selected.domain || '');
  const selectedUrl = selected.frontendUrl?.trim().replace(/\/$/, '') ||
    (selectedHost ? `https://${selectedHost}` : '');

  let next = content;
  const others = sites
    .filter((site) => site.id !== selectedSiteId)
    .flatMap((site) => {
      const host = normalizeHost(site.frontendUrl || site.domain || '');
      const url = site.frontendUrl?.trim().replace(/\/$/, '') || (host ? `https://${host}` : '');
      return [
        { from: site.label, to: selectedLabel },
        { from: url, to: selectedUrl },
        { from: `https://www.${host}`, to: selectedUrl },
        { from: `http://www.${host}`, to: selectedUrl },
        { from: `https://${host}`, to: selectedUrl },
        { from: `http://${host}`, to: selectedUrl },
        { from: host, to: selectedHost || selectedLabel },
      ].filter((row) => row.from && row.to && row.from.toLowerCase() !== row.to.toLowerCase());
    })
    .sort((a, b) => b.from.length - a.from.length);

  const seen = new Set<string>();
  for (const row of others) {
    const key = row.from.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    next = next.replace(new RegExp(escapeRegExp(row.from), 'gi'), row.to);
  }

  return next;
}
